import React, { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Line from "../../component/ui/Line"; // Adjust path to your Line component

gsap.registerPlugin(ScrollTrigger);

const AnimatedFeatureSection = ({
  subtitle,
  title,
  features,
  bgColor = "bg-[#1C233A]",
  textColor = "text-text-secondary",
  lineColor = "bg-text-secondary",
}) => {
  const sectionRef = useRef(null);

  useEffect(() => {
    if (!sectionRef.current) return;

    const ctx = gsap.context(() => {
      // --- 1. Text Reveal Animation ---
      const textElements = gsap.utils.toArray(".wcu-animated-text");

      textElements.forEach((element, index) => {
        gsap.set(element, {
          y: 25,
          opacity: 0,
          clipPath: "inset(100% 0 0% 0)",
        });

        gsap.to(element, {
          y: 0,
          opacity: 1,
          clipPath: "inset(0% 0 0% 0)",
          duration: 1,
          ease: "power2.out",
          delay: index * 0.05, // Slight staggered delay
          scrollTrigger: {
            trigger: element.closest(".wcu-animate-trigger") || element,
            start: "top 85%",
            end: "center 60%",
            scrub: 1,
          },
        });
      });

      // --- 2. Divider Line Animation ---
      const lineElements =
        sectionRef.current.querySelectorAll(".wcu-divider-line");
      lineElements.forEach((line) => {
        gsap.set(line, { scaleX: 0, transformOrigin: "left" });
        gsap.to(line, {
          scaleX: 1,
          duration: 1,
          ease: "none",
          scrollTrigger: {
            trigger: line.closest(".wcu-subtitle-wrapper") || line,
            start: "top 85%",
            end: "center 60%",
            scrub: 1,
          },
        });
      });

      // --- 3. Feature Cards Fade-Up Animation ---
      const cardElements = sectionRef.current.querySelectorAll(".wcu-card");
      cardElements.forEach((card) => {
        gsap.set(card, { y: 40, opacity: 0 });
        gsap.to(card, {
          y: 0,
          opacity: 1,
          duration: 1,
          ease: "power2.out",
          scrollTrigger: {
            trigger: card,
            start: "top 85%",
            end: "center 60%",
            scrub: 1,
          },
        });
      });
    }, sectionRef);

    return () => ctx.revert();
  }, [features]); // Re-run if features change

  return (
    <section ref={sectionRef} className={`w-full ${bgColor} font-[Sora]`}>
      <div className="w-full flex flex-col gap-[37px] 2xl:gap-[74px] justify-center items-center py-[60px] 2xl:py-[120px] max-w-[1612px] mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="flex flex-col gap-[9px] 2xl:gap-[18px] justify-start items-start w-full">
          {/* Subtitle & Line */}
          <div className="flex flex-row justify-start items-center w-full wcu-subtitle-wrapper overflow-hidden wcu-animate-trigger">
            <span
              className={`text-[16px] 2xl:text-[24px] font-normal leading-xl text-left ${textColor} wcu-animated-text`}
            >
              {subtitle}
            </span>
            <Line
              className={`ml-[15px] 2xl:ml-[30px] ${lineColor} wcu-divider-line`}
              width="100px"
              height="2px"
            />
          </div>

          {/* Main Title */}
          <div className="overflow-hidden wcu-animate-trigger">
            <h2
              className={`text-[30px] 2xl:text-[60px] font-bold ${textColor} width-full wcu-animated-text`}
            >
              {title}
            </h2>
          </div>
        </div>

        {/* Features Grid */}
        <div className="max-w-container mx-auto w-full">
          <div className="grid grid-cols-1 sm:grid-cols-2 2xl:grid-cols-4 gap-[35px] 2xl:gap-[70px] w-full">
            {features?.map((feature, index) => (
              <div
                key={index}
                // We append the custom className from the data object here to handle the specific margins
                className={`flex flex-col justify-start items-start w-full 2xl:max-w-[350px] wcu-card ${
                  feature?.className || ""
                }`}
              >
                {/* Icon */}
                {feature.icon && (
                  <img
                    src={feature.icon}
                    alt={feature.title}
                    className="w-[25px] h-[25px] 2xl:w-[50px] 2xl:h-[50px]"
                  />
                )}

                {/* Card Title */}
                <div className="overflow-hidden wcu-animate-trigger w-full">
                  <h3
                    className={`text-[20px] 2xl:text-[30px] font-bold leading-lg text-left ${textColor} w-full mt-[12px] 2xl:mt-[24px] wcu-animated-text`}
                  >
                    {feature.title}
                  </h3>
                </div>

                {/* Card Description */}
                <div className="overflow-hidden wcu-animate-trigger w-full">
                  <p
                    className={`text-[16px] 2xl:text-[24px] font-normal leading-lg text-justify ${textColor} w-full mt-[7px] 2xl:mt-[14px] wcu-animated-text`}
                  >
                    {feature.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default AnimatedFeatureSection;
